import { io, Socket } from 'socket.io-client';
import { refreshAccessToken } from './tokenRefresh';

const WS_URL = '';

export type ConnectionStatusCallback = (connected: boolean, reconnecting: boolean) => void;

// Calculate exponential backoff with jitter
const calculateBackoff = (attemptNumber: number): number => {
  const baseDelay = 1000; // 1 second
  const maxDelay = 30000; // 30 seconds
  const exponentialDelay = Math.min(baseDelay * Math.pow(2, attemptNumber), maxDelay);
  // Add random jitter (±20%)
  const jitter = exponentialDelay * 0.2 * (Math.random() - 0.5);
  return Math.max(100, exponentialDelay + jitter);
};

class SocketService {
  private socket: Socket | null = null;
  private connectionCallbacks: Set<ConnectionStatusCallback> = new Set();
  private authRetryCallbacks: Set<(retrying: boolean) => void> = new Set();
  private wsUrl: string = WS_URL;
  private reconnectAttempt: number = 0;
  private joinedChannels: Set<string> = new Set();
  private pageHiddenHandler: (() => void) | null = null;
  private triedAuthRefresh: boolean = false;
  private emitQueue: Array<{ event: string; args: any[] }> = [];

  constructor() {
    // Empty constructor for web, we just use relative paths
  }

  async connect() {
    if (this.socket?.connected) {
      this.notifyConnectionCallbacks(true, false);
      return;
    }

    const token = localStorage.getItem('accessToken');
    if (!token) {
      console.warn('Socket connect failed: No access token');
      return;
    }

    this.socket = io(this.wsUrl, {
      auth: { token },
      transports: ['websocket', 'polling'], // Allow fallback to polling
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      timeout: 10000,
      autoConnect: true,
    });

    this.socket.on('connect', () => {
      console.info('[SocketService] Connected to WebSocket server.');
      this.reconnectAttempt = 0;
      this.triedAuthRefresh = false;
      this.notifyConnectionCallbacks(true, false);
      this.notifyAuthRetryCallbacks(false);
      this.rejoinChannels();
      this.flushEmitQueue();
    });

    this.socket.on('disconnect', (reason) => {
      console.warn('[SocketService] Disconnected from WebSocket server:', reason);
      // Don't reconnect if user manually disconnected or server forcefully disconnected
      if (reason === 'io server disconnect') {
        this.socket?.connect(); // Server requested disconnect, likely due to auth issue
      } else {
        this.notifyConnectionCallbacks(false, true);
      }
    });

    this.socket.on('connect_error', async (error: any) => {
      console.error('[SocketService] Connection error:', error.message);

      // Handle authentication errors
      if (error.message?.includes('Authentication') || error.data?.type === 'UnauthorizedError') {
        if (!this.triedAuthRefresh) {
          this.triedAuthRefresh = true;
          this.notifyAuthRetryCallbacks(true);

          try {
            const newToken = await refreshAccessToken();
            if (newToken && this.socket) {
              this.socket.auth = { token: newToken };
              this.socket.connect();
              return;
            }
          } catch (refreshError) {
            console.error('[SocketService] Token refresh failed for socket:', refreshError);
            this.notifyAuthRetryCallbacks(false);
          }
        }
      }

      this.reconnectAttempt++;
      const delay = calculateBackoff(this.reconnectAttempt);

      this.notifyConnectionCallbacks(false, true);

      // Implement manual backoff for standard connection errors
      setTimeout(() => {
        if (!this.socket?.connected) {
          this.socket?.connect();
        }
      }, delay);
    });

    // Handle visibility changes to reconnect when app comes to foreground
    this.setupVisibilityHandler();
  }

  private setupVisibilityHandler() {
    if (this.pageHiddenHandler) {
      document.removeEventListener('visibilitychange', this.pageHiddenHandler);
    }

    this.pageHiddenHandler = () => {
      if (document.visibilityState === 'visible' && this.socket?.disconnected) {
        console.log('[SocketService] App foregrounded, attempting to reconnect socket');
        this.connect();
      }
    };

    document.addEventListener('visibilitychange', this.pageHiddenHandler);
  }

  onConnectionChange(callback: ConnectionStatusCallback) {
    this.connectionCallbacks.add(callback);
    if (this.socket) {
      callback(this.socket.connected, !this.socket.connected && this.reconnectAttempt > 0);
    }
    return () => {
      this.connectionCallbacks.delete(callback);
    };
  }

  onAuthRetryChange(callback: (retrying: boolean) => void) {
    this.authRetryCallbacks.add(callback);
    callback(this.triedAuthRefresh);
    return () => {
      this.authRetryCallbacks.delete(callback);
    };
  }

  private notifyConnectionCallbacks(connected: boolean, reconnecting: boolean) {
    this.connectionCallbacks.forEach((cb) => cb(connected, reconnecting));
  }

  private notifyAuthRetryCallbacks(retrying: boolean) {
    this.authRetryCallbacks.forEach((cb) => cb(retrying));
  }

  private rejoinChannels() {
    this.joinedChannels.forEach((channelId) => {
      this.socket?.emit('channel:join', channelId);
      this.socket?.emit('join:channel', channelId);
    });
  }

  joinChannel(channelId: string) {
    this.joinedChannels.add(channelId);
    if (this.socket?.connected) {
      this.socket.emit('channel:join', channelId);
      this.socket.emit('join:channel', channelId);
    }
  }

  leaveChannel(channelId: string) {
    this.joinedChannels.delete(channelId);
    if (this.socket?.connected) {
      this.socket.emit('channel:leave', channelId);
      this.socket.emit('leave:channel', channelId);
    }
  }

  startTyping(channelId: string) {
    if (this.socket?.connected) {
      this.socket.emit('typing:start', channelId);
    }
  }

  stopTyping(channelId: string) {
    if (this.socket?.connected) {
      this.socket.emit('typing:stop', channelId);
    }
  }

  sendMessage(channelId: string, content: string, replyToId?: string) {
    if (this.socket?.connected) {
      this.socket.emit('message:send', { channelId, content, replyToId });
    }
  }

  on(event: string, callback: (...args: any[]) => void) {
    if (!this.socket) {
      this.connect().then(() => this.socket?.on(event, callback));
      return;
    }
    this.socket.on(event, callback);
  }

  off(event: string, callback?: (...args: any[]) => void) {
    if (this.socket) {
      if (callback) {
        this.socket.off(event, callback);
      } else {
        this.socket.off(event);
      }
    }
  }

  private flushEmitQueue() {
    if (!this.socket?.connected || this.emitQueue.length === 0) return;
    const queue = [...this.emitQueue];
    this.emitQueue = [];
    queue.forEach(({ event, args }) => {
      this.socket?.emit(event, ...args);
    });
  }

  emit(event: string, ...args: any[]) {
    if (this.socket?.connected) {
      this.socket.emit(event, ...args);
    } else {
      // Buffer event to send as soon as connected
      this.emitQueue.push({ event, args });
    }
  }

  disconnect() {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }
    if (this.pageHiddenHandler) {
      document.removeEventListener('visibilitychange', this.pageHiddenHandler);
      this.pageHiddenHandler = null;
    }
    this.joinedChannels.clear();
    this.emitQueue = [];
    this.reconnectAttempt = 0;
    this.notifyConnectionCallbacks(false, false);
  }

  isConnected() {
    return this.socket?.connected ?? false;
  }
}

export default new SocketService();
